'use client';

import Link from 'next/link';
import {Lock} from 'lucide-react';
import {CompanyLogo} from 'iq-interview_guides/components/company_logo';
import {OutcomeBadge} from 'iq-interview_experiences/components/outcome_badge';
import {DotSeparator} from 'common/components/dot_separator';
import {formatDateToQuarter} from 'common/utils';
import {trackFeedCardClicked} from 'iq-companies/tracking';
import type {FeedExperienceItem} from 'iq-companies/types';

// CIG (company-level guide) takes priority; falls back to the legacy position-level
// guide for companies with no CIG. Returns null when neither exists — is_locked is
// only ever true when guide_slug is set (see backend FeedItem doc), so the locked
// branch below always has a real href.
function guideHref(item: FeedExperienceItem): string | null {
  if (item.guide_slug) return `/interview-guides/${item.guide_slug}`;
  if (item.position_guide_slug) return `/guides/${item.position_guide_slug}`;
  return null;
}

// The unlock modal (UnlockInterviewGuide, via `?unlock=1`) only auto-opens on the
// legacy /guides/[slug] page today — the CIG page (/interview-guides/[slug]) doesn't
// read the param yet, same gap the CIG page's own recent_experience_card.tsx works
// around by bouncing to the position guide. Mirror that here: prefer the position
// guide (real modal popup) when one exists, otherwise fall straight through to the
// CIG page with no query param (it'll show its own locked experience cards there).
function unlockHref(item: FeedExperienceItem): string | null {
  if (item.position_guide_slug) return `/guides/${item.position_guide_slug}?unlock=1`;
  return guideHref(item);
}

function FeedCardContent({item, href}: {item: FeedExperienceItem; href: string | null}) {
  return (
    <>
      <div className='flex flex-wrap items-center gap-2 text-xs'>
        <span className='font-semibold uppercase tracking-wide text-iq-orange'>
          Interview Experience
        </span>
        <DotSeparator className='text-gray-300' />
        <CompanyLogo
          companyWebsite={item.company_website}
          companyName={item.company_name}
          size={16}
          className='rounded object-contain'
        />
        <span className='font-medium text-gray-700'>{item.company_name}</span>
        <DotSeparator className='text-gray-300' />
        <span className='text-gray-500'>Interviewed {formatDateToQuarter(item.interview_date)}</span>
        {item.is_new && (
          <span className='ml-1 rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-700'>
            New
          </span>
        )}
        {item.outcome && (
          <span className='ml-auto'>
            <OutcomeBadge outcome={item.outcome} outcome_color={item.outcome_color} />
          </span>
        )}
      </div>

      <p className='mt-2 font-semibold text-gray-900'>{item.position_display_name}</p>
      <p className='mt-1 line-clamp-3 text-sm text-gray-600'>{item.preview}</p>

      {href && (
        <span className='mt-3 inline-flex items-center gap-1 text-sm font-semibold text-iq-orange'>
          {item.is_locked ? (
            <>
              <Lock className='h-3.5 w-3.5' />
              Share an experience to unlock the full {item.company_name} report
            </>
          ) : (
            <>View {item.company_name} interview guide →</>
          )}
        </span>
      )}
    </>
  );
}

// Cards link out instead of blocking inline — locked cards route through the same
// share-to-unlock modal used elsewhere in the app rather than rendering a dead-end
// gate box, so the click is a normal, trackable navigation. The whole card is
// clickable, matching RecencyFeedCard/QuestionFeedCard.
export function FeedCard({item}: {item: FeedExperienceItem}) {
  const href = item.is_locked ? unlockHref(item) : guideHref(item);
  const cardClassName = 'block rounded-xl border border-gray-200 bg-white p-5';

  if (!href) {
    return (
      <div className={cardClassName}>
        <FeedCardContent item={item} href={href} />
      </div>
    );
  }

  return (
    <Link
      href={href}
      onClick={() => void trackFeedCardClicked({id: item.id, company_slug: item.company_slug, is_locked: item.is_locked})}
      className={cardClassName}>
      <FeedCardContent item={item} href={href} />
    </Link>
  );
}
